"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import type { MentorTask, CoveyQuadrant, MentorRecurringTask } from "@/lib/mentorTypes";
import { isRoutine } from "@/lib/mentor/taskCharacter";
import TaskCard from "./TaskCard";
import TaskEditorModal from "./TaskEditorModal";
import TaskCreateModal from "./TaskCreateModal";
import RecurringTaskModal from "./RecurringTaskModal";
import RecurringTasksPanel from "./RecurringTasksPanel";
import SoortKeuze from "./SoortKeuze";
import { TaakRij, TaakSheet, Icoon, IcoonKnop, Segment, Q_SEM, SEM, ddmm, prioSem, DomeinLabel, DOMEINEN, DOMEIN_KLEUR } from "@/components/ui/mobiel";

interface TaskBoardProps {
  tasks: MentorTask[];
  onTasksChange: () => void;
}

type BoardView = "covey" | "agenda";

// ADR-0083 — het kwadrantenbord blijft vier kolommen (hier sleept Jorn zelf), maar de koppen
// heten voortaan in mensentaal en tonen geen Q-code meer.
const COLS: { key: CoveyQuadrant; label: string; sub: string; textCls: string; dropCls: string }[] = [
  // ADR-0099: kolomkoppen in label-zwart; alleen "Nu" mag rood. Sleep-doel = accent-ring.
  { key: "Q1", label: "Nu",         sub: "Urgent & belangrijk",        textCls: "text-[var(--sem-overdue)]", dropCls: "ring-[var(--accent)]/40 bg-[var(--accent-soft)]" },
  { key: "Q2", label: "Inplannen",  sub: "Niet urgent & belangrijk",   textCls: "text-[var(--ds-text)]",     dropCls: "ring-[var(--accent)]/40 bg-[var(--accent-soft)]" },
  { key: "Q3", label: "Kan wachten",sub: "Urgent & onbelangrijk",      textCls: "text-[var(--ds-text)]",     dropCls: "ring-[var(--accent)]/40 bg-[var(--accent-soft)]" },
  { key: "Q4", label: "Laten",      sub: "Niet urgent & onbelangrijk", textCls: "text-[var(--ds-text-soft)]", dropCls: "ring-[var(--accent)]/40 bg-[var(--accent-soft)]" },
];

const PRIO_ORDER: Record<string, number> = { P0: 0, P1: 1, P2: 2, P3: 3 };
// Covey-kwadrant → bijpassende prioriteit (zoals de auto-engine: Q1=P0 … Q4=P3).
const QUADRANT_PRIO: Record<CoveyQuadrant, "P0" | "P1" | "P2" | "P3"> = { Q1: "P0", Q2: "P1", Q3: "P2", Q4: "P3" };

function sortTasks(list: MentorTask[]): MentorTask[] {
  return [...list].sort((a, b) => (b.rang ?? 0) - (a.rang ?? 0));
}

// ── Agenda helpers ─────────────────────────────────────────────────────────────

type ScheduleType = "timed" | "scheduled" | "deadline" | "flexible";

function getScheduleType(task: MentorTask): ScheduleType {
  if (task.source === "calendar") return "timed";
  if (task.plannedStart) return "scheduled";
  if (task.hardDeadline ?? task.deadline) return "deadline";
  return "flexible";
}

function getTaskSortDate(task: MentorTask): string {
  return task.plannedStart?.slice(0, 10)
    ?? task.hardDeadline
    ?? task.softDeadline
    ?? task.deadline
    ?? "9999-12-31";
}

interface AgendaGroup {
  key: string;
  label: string;
  headerCls: string;
  tasks: MentorTask[];
}

const NL_DAYS   = ["zo", "ma", "di", "wo", "do", "vr", "za"];
const NL_MONTHS = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

function fmtDateShort(iso: string): string {
  const d = new Date(iso + "T12:00:00");
  return `${NL_DAYS[d.getDay()]} ${d.getDate()} ${NL_MONTHS[d.getMonth()]}`;
}

function addDaysISO(base: Date, n: number): string {
  const d = new Date(base);
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString("sv-SE", { timeZone: "Europe/Amsterdam" });
}

function getAgendaGroups(tasks: MentorTask[]): AgendaGroup[] {
  const now      = new Date();
  const today    = now.toLocaleDateString("sv-SE", { timeZone: "Europe/Amsterdam" });
  const in3days  = addDaysISO(now, 3);
  const in7days  = addDaysISO(now, 7);
  const in14days = addDaysISO(now, 14);

  const buckets: Record<string, MentorTask[]> = {
    overdue: [], today: [], soon: [], thisWeek: [], later2w: [], later: [], flexible: [],
  };

  for (const task of tasks) {
    const sd = getTaskSortDate(task);
    if (sd === "9999-12-31")  buckets.flexible.push(task);
    else if (sd < today)      buckets.overdue.push(task);
    else if (sd === today)    buckets.today.push(task);
    else if (sd <= in3days)   buckets.soon.push(task);
    else if (sd <= in7days)   buckets.thisWeek.push(task);
    else if (sd <= in14days)  buckets.later2w.push(task);
    else                      buckets.later.push(task);
  }

  function sortBucket(list: MentorTask[]) {
    return [...list].sort((a, b) => {
      const da = getTaskSortDate(a), db = getTaskSortDate(b);
      if (da !== db) return da.localeCompare(db);
      return (b.rang ?? 0) - (a.rang ?? 0);
    });
  }

  return [
    { key: "overdue",  label: "Te laat",                                      headerCls: "text-red-500",   tasks: sortBucket(buckets.overdue)  },
    { key: "today",    label: `Vandaag — ${fmtDateShort(today)}`,              headerCls: "text-red-600",   tasks: sortBucket(buckets.today)    },
    { key: "soon",     label: `Binnenkort — t/m ${fmtDateShort(in3days)}`,    headerCls: "text-amber-700", tasks: sortBucket(buckets.soon)     },
    { key: "thisWeek", label: `Deze week — t/m ${fmtDateShort(in7days)}`,     headerCls: "text-blue-700",  tasks: sortBucket(buckets.thisWeek) },
    { key: "later2w",  label: "Volgende week",                                 headerCls: "text-zinc-600",  tasks: sortBucket(buckets.later2w)  },
    { key: "later",    label: "Later",                                         headerCls: "text-zinc-600",  tasks: sortBucket(buckets.later)    },
    { key: "flexible", label: "Flexibel — geen datum",                         headerCls: "text-zinc-600",  tasks: sortBucket(buckets.flexible) },
  ].filter(g => g.tasks.length > 0);
}

// ── Agenda row ────────────────────────────────────────────────────────────────

const TYPE_ICON: Record<ScheduleType, { icon: string; cls: string; label: string }> = {
  timed:    { icon: "◆", cls: "text-red-600",     label: "Vaste afspraak" },
  scheduled:{ icon: "●", cls: "text-emerald-700", label: "Ingepland" },
  deadline: { icon: "▲", cls: "text-amber-700",   label: "Deadline" },
  flexible: { icon: "○", cls: "text-zinc-600",    label: "Flexibel" },
};

interface AgendaRowProps {
  task: MentorTask;
  onEdit: (t: MentorTask) => void;
  onComplete: (id: string) => void;
  onPark: (id: string) => void;
}

function AgendaTaskRow({ task, onEdit, onComplete, onPark }: AgendaRowProps) {
  const type = getScheduleType(task);
  const ti   = TYPE_ICON[type];
  const dl   = task.hardDeadline ?? task.deadline;

  let dateStr = "";
  if (task.plannedStart) {
    dateStr = new Date(task.plannedStart).toLocaleString("nl-NL", {
      weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/Amsterdam",
    });
  } else if (dl) {
    dateStr = `dl: ${fmtDateShort(dl)}`;
  }

  return (
    <div
      onClick={() => onEdit(task)}
      className="flex items-center gap-2 px-3 py-2.5 hover:bg-[var(--ds-hover)] active:bg-[var(--ds-hover)] cursor-pointer transition-colors border-b border-[var(--ds-hairline)] last:border-b-0"
    >
      <span className={`text-sm shrink-0 ${ti.cls}`} title={ti.label}>{ti.icon}</span>
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: SEM[prioSem(task)].c }} />
      <span className="text-xs text-[var(--ds-text)] flex-1 truncate">{task.title}</span>
      {dateStr && (
        <span className="text-[10px] text-[var(--ds-text-faint)] font-mono shrink-0 hidden sm:inline">{dateStr}</span>
      )}
      {/* Altijd zichtbaar (ook op touch) + grote tikvlakken */}
      <div className="flex gap-1.5 shrink-0">
        <button
          onClick={e => { e.stopPropagation(); onComplete(task.id); }}
          className="w-11 h-11 flex items-center justify-center text-sm rounded-xl border border-emerald-600/50 text-emerald-600 hover:bg-emerald-500/10 active:scale-95 transition-transform"
          title="Klaar"
        ><Icoon naam="check" size={16} /></button>
        <button
          onClick={e => { e.stopPropagation(); onPark(task.id); }}
          className="w-11 h-11 flex items-center justify-center text-sm rounded-xl border border-[var(--ds-hairline)] text-[var(--ds-text-soft)] hover:text-amber-700 active:scale-95 transition-transform"
          title="Parkeren"
        ><Icoon naam="pauze" size={16} /></button>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function TaskBoard({ tasks, onTasksChange }: TaskBoardProps) {
  const [search, setSearch]                         = useState("");
  const [viewMode, setViewMode]                     = useState<BoardView>("covey");
  const [editingTask, setEditingTask]               = useState<MentorTask | null>(null);
  const [showCreateModal, setShowCreateModal]       = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [editingRecurring, setEditingRecurring]     = useState<MentorRecurringTask | null>(null);
  const [recurringTasks, setRecurringTasks]         = useState<MentorRecurringTask[]>([]);
  const [draggedId, setDraggedId]                   = useState<string | null>(null);
  const [dragOverCol, setDragOverCol]               = useState<CoveyQuadrant | null>(null);
  // ADR-0081 — mobiel: kleur-segment + kale lijst + detail-sheet; "+" opent een keuze-sheet
  const [mobielQ, setMobielQ]                       = useState<CoveyQuadrant>("Q1");
  const [sheetTaak, setSheetTaak]                   = useState<MentorTask | null>(null);
  const [domeinFilter, setDomeinFilter]             = useState<string | null>(null); // ADR-0092: null = alles, "geen" = nog niet ingedeeld
  const [showPlus, setShowPlus]                     = useState(false);
  const [showZoek, setShowZoek]                     = useState(false);
  // Restyle Taken-tab (grill 9 okt 2026): filter in één knop, lijsten onderin als sheets
  const [showFilter, setShowFilter]                 = useState(false);
  const [sheetLijst, setSheetLijst]                 = useState<"menu" | "afspraken" | "routines" | "archief" | null>(null);
  const leaveTimers                                 = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const loadRecurring = useCallback(async () => {
    try {
      const res  = await fetch("/api/recurring-tasks");
      const data = await res.json() as { recurringTasks: MentorRecurringTask[] };
      setRecurringTasks(data.recurringTasks ?? []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadRecurring(); }, [loadRecurring]);

  async function api(path: string, method = "POST", body?: unknown) {
    await fetch(path, {
      method,
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body:    body ? JSON.stringify(body) : undefined,
    });
    onTasksChange();
  }

  const handleComplete = useCallback((id: string) => api(`/api/tasks/${id}/complete`),                      [onTasksChange]);
  const handleCancel   = useCallback((id: string) => api(`/api/tasks/${id}/cancel`),                        [onTasksChange]);
  const handlePark     = useCallback((id: string) => api(`/api/tasks/${id}/park`, "POST", { reason: "" }), [onTasksChange]);
  const handleReopen   = useCallback((id: string) => api(`/api/tasks/${id}/reopen`),                        [onTasksChange]);
  const handlePriority = useCallback((id: string, priority: string) => api(`/api/tasks/${id}`, "PATCH", { priority }), [onTasksChange]);

  async function handleSaveEdit(updated: Partial<MentorTask>) {
    if (!editingTask) return;
    const patch: Partial<MentorTask> = { ...updated };
    // Kwadrant in de editor gewijzigd → handmatig vastzetten (+ bijpassende prioriteit).
    if (updated.coveyQuadrant && updated.coveyQuadrant !== (editingTask.coveyQuadrant ?? "Q2")) {
      patch.coveyManual = true;
      patch.priority = QUADRANT_PRIO[updated.coveyQuadrant];
    }
    await api(`/api/tasks/${editingTask.id}`, "PATCH", patch);
    setEditingTask(null);
  }

  // ADR-0092 — Jorn corrigeert het domein met één tik; daarna blijft de automatische indeling eraf.
  async function zetDomein(id: string, d: string) {
    setSheetTaak(s => s && s.id === id ? { ...s, project: d, domeinBron: "jorn" } : s);
    await api(`/api/tasks/${id}`, "PATCH", { project: d });
  }

  async function moveToQuadrant(id: string, q: CoveyQuadrant) {
    const task = tasks.find(t => t.id === id);
    if (!task || (task.coveyQuadrant ?? "Q2") === q) return;
    // Handmatig: kwadrant + bijpassende prioriteit + override-vlag (blijft plakken).
    await api(`/api/tasks/${id}`, "PATCH", { coveyQuadrant: q, coveyManual: true, priority: QUADRANT_PRIO[q] });
  }

  async function handleDrop(q: CoveyQuadrant) {
    const id = draggedId;
    setDraggedId(null);
    setDragOverCol(null);
    if (id) await moveToQuadrant(id, q);
  }

  // ── Touch-slepen tussen kwadranten ──────────────────────────────────────────
  // HTML5-drag werkt niet op touch/iOS, dus voor de vinger gebruiken we pointer events:
  // even vasthouden (200ms) pakt de kaart op, dan sleep je 'm naar een ander kwadrant.
  // Muis blijft de bestaande HTML5-drag gebruiken.
  const coveyScrollRef = useRef<HTMLDivElement>(null);
  const [touchDrag, setTouchDrag] = useState<{ title: string; x: number; y: number; q: CoveyQuadrant | null } | null>(null);

  function onCardPointerDown(e: React.PointerEvent, task: MentorTask) {
    if (e.pointerType !== "touch") return;                       // muis → HTML5-drag
    if ((e.target as HTMLElement).closest("button")) return;     // knoppen niet kapen
    const startX = e.clientX, startY = e.clientY;
    const session = { active: false, targetQ: (task.coveyQuadrant ?? "Q2") as CoveyQuadrant };
    let timer: ReturnType<typeof setTimeout>;
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
    };
    const move = (ev: PointerEvent) => {
      if (!session.active) {
        if (Math.abs(ev.clientX - startX) > 12 || Math.abs(ev.clientY - startY) > 12) cleanup(); // scrollen
        return;
      }
      ev.preventDefault();
      const el = document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null;
      const q = (el?.closest("[data-quadrant]")?.getAttribute("data-quadrant") ?? null) as CoveyQuadrant | null;
      if (q) session.targetQ = q;
      setDragOverCol(q);
      setTouchDrag({ title: task.title, x: ev.clientX, y: ev.clientY, q });
      const sc = coveyScrollRef.current;
      if (sc) {
        const r = sc.getBoundingClientRect();
        if (ev.clientY < r.top + 64) sc.scrollTop -= 14;
        else if (ev.clientY > r.bottom - 64) sc.scrollTop += 14;
      }
    };
    const finish = () => {
      const wasActive = session.active;
      cleanup();
      setTouchDrag(null);
      setDragOverCol(null);
      if (wasActive) void moveToQuadrant(task.id, session.targetQ);
    };
    timer = setTimeout(() => {
      session.active = true;
      setTouchDrag({ title: task.title, x: startX, y: startY, q: task.coveyQuadrant ?? "Q2" });
      try { (navigator as Navigator & { vibrate?: (n: number) => void }).vibrate?.(12); } catch { /* ignore */ }
    }, 200);
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
  }

  // Routine-instances horen niet in Covey/agenda — die staan in de Routines-accordion
  // (template) en leven verder alleen als planbaar blok in de planner.
  // ADR-0091 — afspraken (ergens zijn, niets produceren) horen niet in de kwadranten maar in een eigen kop
  const isAfspraak = (t: MentorTask) => t.taskKind === "appointment";
  // ADR-0092 — filter op domein (label + filter, de planning blijft ongemoeid)
  const inDomein = (t: MentorTask) => domeinFilter === null ? true : domeinFilter === "geen" ? !t.project || !DOMEIN_KLEUR[t.project] : t.project === domeinFilter;
  const afspraken = tasks.filter(t => (t.status === "open" || t.status === "in_progress") && !isRoutine(t) && isAfspraak(t) && t.plannedStart && inDomein(t)
    && (t.plannedEnd ?? t.plannedStart) >= new Date(Date.now() - 2 * 3600_000).toISOString().slice(0, 13))
    .sort((a, b) => (a.plannedStart ?? "").localeCompare(b.plannedStart ?? ""));
  const active = tasks.filter(t => (t.status === "open" || t.status === "in_progress") && !isRoutine(t) && !isAfspraak(t));
  const done   = tasks.filter(t => (t.status === "done" || t.status === "cancelled") && !isRoutine(t));
  const parked = tasks.filter(t => t.status === "parked" && !isRoutine(t));   // grill 3 sep: nooit meer onzichtbaar

  function filteredActive(): MentorTask[] {
    const basis = active.filter(inDomein);
    if (!search) return basis;
    const s = search.toLowerCase();
    return basis.filter(t =>
      t.title.toLowerCase().includes(s) ||
      (t.project ?? "").toLowerCase().includes(s) ||
      (t.tags ?? []).some(tag => tag.toLowerCase().includes(s))
    );
  }

  function colTasks(q: CoveyQuadrant): MentorTask[] {
    return sortTasks(filteredActive().filter(t => (t.coveyQuadrant ?? "Q2") === q));
  }

  const agendaGroups = viewMode === "agenda" ? getAgendaGroups(filteredActive()) : [];
  const vandaagISO = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Amsterdam" });
  const in3dISO    = new Date(Date.now() + 3 * 86400000).toLocaleDateString("sv-SE", { timeZone: "Europe/Amsterdam" });
  const isLedgndTaak = (t: MentorTask) => t.source === "ledgnd" || (t.sources ?? []).some(s => s.source === "ledgnd");

  return (
    <>
      <div className="flex flex-col h-full min-h-0 bg-transparent min-w-0 overflow-x-hidden">

        {/* ── Mobiel (restyle 9 okt 2026): rustige kop — zoeken en één filterknop; daaronder ruime kleurpills ── */}
        {(() => {
          const open = tasks.filter(t => (t.status === "open" || t.status === "in_progress") && !isRoutine(t));
          const telling = (d: string | null) => open.filter(t => d === null ? true : d === "geen" ? !t.project || !DOMEIN_KLEUR[t.project] : t.project === d).length;
          const opties: { key: string | null; label: string }[] = [{ key: null, label: "Alles" }, ...DOMEINEN.map(d => ({ key: d as string, label: d })), ...(telling("geen") ? [{ key: "geen", label: "Zonder domein" }] : [])];
          const filterAan = domeinFilter !== null;
          const filterNaam = domeinFilter === "geen" ? "Zonder domein" : domeinFilter;
          return (
            <>
              <div className="sm:hidden bg-[var(--ds-panel)] shrink-0">
                <div className="flex items-center gap-2 px-4 pt-2 pb-1">
                  {showZoek ? (
                    <>
                      <input type="text" autoFocus placeholder="Zoek in taken" value={search} onChange={e => setSearch(e.target.value)}
                        className="flex-1 min-w-0 min-h-[44px] px-4 rounded-full text-[16px] bg-[var(--ds-surface)] text-[var(--ds-text)] ring-1 ring-[var(--ds-hairline)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] placeholder:text-[var(--ds-text-faint)]" />
                      <button onClick={() => { setShowZoek(false); setSearch(""); }} className="min-h-[44px] px-2 text-[15px] font-semibold shrink-0" style={{ color: "var(--accent)" }}>Klaar</button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 min-w-0 text-[13px] text-[var(--ds-text-faint)] truncate">
                        {open.length} open{filterAan ? ` · ${filterNaam}` : ""}
                      </span>
                      <IcoonKnop naam="zoek" onClick={() => setShowZoek(true)} label="Zoeken" />
                      {/* Zelfde maat als de zoekknop (44px tikvlak, 36px zichtbaar); filter aan = donker vlak. */}
                      <button onClick={() => setShowFilter(true)} aria-label="Filter" aria-pressed={filterAan}
                        className="w-11 h-11 flex items-center justify-center shrink-0 active:opacity-60 transition-opacity">
                        <span className="w-9 h-9 rounded-full flex items-center justify-center"
                          style={filterAan ? { background: "var(--ds-text)", color: "var(--ds-panel)" } : { background: "var(--ds-hover)", color: "var(--ds-text)" }}>
                          <Icoon naam="filter" size={18} />
                        </span>
                      </button>
                      {/* 10 okt 2026 (Jorn: "prop die ergens weg onder een tapje"): afspraken, routines, geparkeerd en gedaan achter één knop */}
                      <IcoonKnop naam="meer" onClick={() => setSheetLijst("menu")} label="Afspraken, routines en gedaan" />
                    </>
                  )}
                </div>
                {/* ADR-0099 — prioriteit als iOS segmented control: alle vier in beeld, geen kleur. */}
                <div className="px-4 pb-3 border-b border-[var(--ds-hairline)]">
                  <Segment label="Prioriteit" waarde={mobielQ} onKies={k => setMobielQ(k)}
                    opties={COLS.map(c => ({ key: c.key, label: <>{c.label} <span className="tabular-nums text-[var(--ds-text-faint)]">{colTasks(c.key).length}</span></> }))} />
                </div>
              </div>

              {/* Filter-sheet (mobiel) */}
              {showFilter && (
                <TaakSheet open onClose={() => setShowFilter(false)} titel="Filter" acties={[
                  ...(filterAan ? [{ label: "Filter wissen", onClick: () => { setDomeinFilter(null); setShowFilter(false); } }] : []),
                  { label: "Klaar", kleur: "accent" as const, onClick: () => setShowFilter(false) },
                ]}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ds-text-faint)] mb-2">Domein</div>
                  <div className="grid grid-cols-2 gap-2">
                    {opties.map(o => { const on = domeinFilter === o.key; const n = telling(o.key); return (
                      <button key={o.label} aria-pressed={on} onClick={() => setDomeinFilter(o.key)}
                        className="min-h-[48px] px-3.5 rounded-xl text-[14px] font-semibold inline-flex items-center gap-2 active:scale-[0.97] transition-transform"
                        style={on ? { background: "var(--ds-text)", color: "var(--ds-panel)" } : { background: "var(--ds-hover)", color: "var(--ds-text)" }}>
                        <span className="flex-1 min-w-0 text-left truncate">{o.label}</span>
                        <span className="tabular-nums" style={{ opacity: 0.6 }}>{n}</span>
                      </button>
                    ); })}
                  </div>
                </TaakSheet>
              )}

              {/* ── Desktop: één rij — nieuwe taak, domeinfilter (ADR-0092), zoeken, weergave, "Meer" (10 okt 2026) ── */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 border-b border-[var(--ds-hairline)] bg-[var(--ds-panel)] shrink-0 flex-wrap">
                <button onClick={() => setShowCreateModal(true)}
                  className="h-8 px-3.5 text-[13px] font-semibold rounded-full bg-[var(--accent)] text-white hover:brightness-105 active:opacity-70 transition-opacity shrink-0">
                  + Nieuwe taak
                </button>
                <div className="flex gap-1.5 min-w-0 overflow-x-auto no-scrollbar" role="tablist" aria-label="Domein">
                  {opties.map(o => { const on = domeinFilter === o.key; const n = telling(o.key); return (
                    <button key={o.label} role="tab" aria-selected={on} onClick={() => setDomeinFilter(o.key)}
                      className="h-8 px-3 rounded-full text-[13px] whitespace-nowrap inline-flex items-center gap-1.5 shrink-0 active:opacity-70 transition-colors duration-200"
                      style={on ? { background: "#1d1d1f", color: "#fff", fontWeight: 600 } : { background: "var(--ds-hover)", color: "var(--ds-text)", fontWeight: 500 }}>
                      {o.label}<span className="tabular-nums" style={{ opacity: on ? 0.7 : 0.5 }}>{n}</span>
                    </button>
                  ); })}
                </div>
                <input type="text" placeholder="Zoeken" value={search} onChange={e => setSearch(e.target.value)}
                  className="flex-1 min-w-[140px] h-8 px-3 text-[13px] bg-[var(--ds-hover)] text-[var(--ds-text)] rounded-full focus:outline-none focus:ring-2 focus:ring-[var(--accent)] placeholder:text-[var(--ds-text-faint)]" />
                <div className="flex rounded-full bg-[var(--ds-hover)] p-0.5 shrink-0" role="tablist" aria-label="Weergave">
                  <button onClick={() => setViewMode("covey")} aria-selected={viewMode === "covey"} role="tab" title="Kwadranten"
                    className={`h-7 px-3 rounded-full text-[13px] transition-colors ${viewMode === "covey" ? "bg-white shadow-sm text-[var(--ds-text)] font-semibold" : "text-[var(--ds-text-soft)]"}`}>Kwadranten</button>
                  <button onClick={() => setViewMode("agenda")} aria-selected={viewMode === "agenda"} role="tab" title="Op datum"
                    className={`h-7 px-3 rounded-full text-[13px] transition-colors ${viewMode === "agenda" ? "bg-white shadow-sm text-[var(--ds-text)] font-semibold" : "text-[var(--ds-text-soft)]"}`}>Op datum</button>
                </div>
                <button onClick={() => setSheetLijst("menu")} title="Afspraken, routines, geparkeerd en gedaan"
                  className="h-8 px-3 rounded-full bg-[var(--ds-hover)] text-[13px] font-medium text-[var(--ds-text)] inline-flex items-center gap-1.5 shrink-0 active:opacity-70 transition-opacity">
                  <Icoon naam="meer" size={16} /> Meer
                </button>
              </div>
            </>
          );
        })()}

        {/* ── ADR-0078: over-deadline-strook — altijd zichtbaar, los van de kwadranten ── */}
        {(() => {
          const vandaag = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Amsterdam" });
          const teLaat = tasks.filter(t => {
            if (t.status !== "open" && t.status !== "in_progress") return false;
            const d = t.hardDeadline ?? t.deadline;
            return d != null && d < vandaag;
          }).sort((a, b) => (a.hardDeadline ?? a.deadline ?? "").localeCompare(b.hardDeadline ?? b.deadline ?? ""));
          if (!teLaat.length) return null;
          return (
            <div className="hidden sm:block px-3 py-2 bg-[var(--ds-panel)] border-b border-[var(--ds-hairline)] shrink-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[12px] font-semibold text-[var(--sem-overdue)] shrink-0">{teLaat.length} over de deadline</span>
                {teLaat.slice(0, 6).map(t => (
                  <button
                    key={t.id}
                    onClick={() => setEditingTask(t)}
                    className="text-[12px] px-2.5 py-1 rounded-full bg-[var(--ds-hover)] text-[var(--ds-text)] hover:brightness-[0.96] active:opacity-70 transition-[filter,opacity] max-w-[240px] truncate"
                    title={`${t.title} — deadline ${(t.hardDeadline ?? t.deadline)}`}
                  >
                    <span className="font-mono font-bold">{(t.hardDeadline ?? t.deadline ?? "").slice(5)}</span> {t.title}
                  </button>
                ))}
                {teLaat.length > 6 && <span className="text-[11px] text-red-600">+{teLaat.length - 6} meer</span>}
              </div>
            </div>
          );
        })()}

        {/* ── Mobiel: lijst van het gekozen kwadrant (ADR-0081) over de hele hoogte; de andere lijsten achter "⋯" ── */}
        <div className="sm:hidden flex-1 min-h-0 overflow-y-auto">
          <div className="divide-y divide-[var(--ds-hairline)] border-b border-[var(--ds-hairline)] bg-[var(--ds-panel)] mb-28">
            {colTasks(mobielQ).length === 0 && (
              <p className="py-14 text-center text-[14px] text-[var(--ds-text-faint)]">{search ? "Niets gevonden" : `Niets onder ${COLS.find(c => c.key === mobielQ)?.label ?? ""}`}</p>
            )}
            {colTasks(mobielQ).map(t => {
              const dl = t.hardDeadline ?? t.deadline;
              return (
                <TaakRij key={t.id} ruim kleur={isLedgndTaak(t) ? "ledgnd" : Q_SEM[mobielQ]} titel={t.title}
                  chip={dl ? ddmm(dl) : undefined} chipKleur={dl ? (dl < vandaagISO ? "overdue" : dl <= in3dISO ? "vandaag" : "binnenkort") : undefined}
                  sub={<DomeinLabel domein={t.project} />}
                  vink={{ aan: false, onTik: () => handleComplete(t.id) }} onOpen={() => setSheetTaak(t)} />
              );
            })}
          </div>
        </div>

        {/* Zwevende + (mobiel), boven de navigatie */}
        <button onClick={() => setShowPlus(true)} aria-label="Nieuw"
          className="sm:hidden fixed right-4 z-[60] w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg active:scale-95 transition-transform"
          style={{ background: "var(--accent, #0071e3)", bottom: "calc(env(safe-area-inset-bottom) + 84px)", boxShadow: "0 6px 18px rgba(0,113,227,0.35)" }}>
          <Icoon naam="plus" size={26} />
        </button>

        {/* "⋯": de lijsten die niet in het dagelijkse beeld horen (mobiel en desktop) */}
        {sheetLijst === "menu" && (
          <TaakSheet open onClose={() => setSheetLijst(null)} titel="Meer">
            <div className="-mx-5 border-y border-[var(--ds-hairline)] divide-y divide-[var(--ds-hairline)]">
              {([
                { key: "afspraken" as const, label: "Afspraken", n: afspraken.length },
                { key: "routines" as const, label: "Routines", n: recurringTasks.filter(t => t.isActive).length },
                ...(parked.length ? [{ key: "archief" as const, label: "Geparkeerd", n: parked.length }] : []),
                { key: "archief" as const, label: "Gedaan", n: done.length },
              ]).map(r => (
                <button key={r.label} onClick={() => setSheetLijst(r.key)} className="w-full min-h-[52px] px-5 flex items-center gap-3 text-left active:bg-[var(--ds-hover)] transition-colors">
                  <span className="flex-1 text-[17px] text-[var(--ds-text)]">{r.label}</span>
                  <span className="text-[15px] tabular-nums text-[var(--ds-text-faint)]">{r.n}</span>
                  <span className="text-[var(--ds-text-faint)]"><Icoon naam="right" size={16} /></span>
                </button>
              ))}
            </div>
          </TaakSheet>
        )}

        {/* Sheets voor de lijsten achter "⋯" */}
        {sheetLijst === "afspraken" && (
          <TaakSheet open onClose={() => setSheetLijst(null)} titel={`Afspraken · ${afspraken.length}`} kleur="vandaag">
            {afspraken.length === 0 ? <p className="text-[14px] text-[var(--ds-text-faint)] py-4 text-center">Geen afspraken</p> : (
              <div className="-mx-5 divide-y divide-[var(--ds-hairline)]">
                {afspraken.map(t => {
                  const d = new Date(t.plannedStart!);
                  const wanneer = d.toLocaleString("nl-NL", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Amsterdam" });
                  return (
                    <button key={t.id} onClick={() => { setSheetLijst(null); setSheetTaak(t); }} className="w-full min-h-[56px] px-5 flex items-center gap-3 text-left active:bg-[var(--ds-hover)]">
                      <span className="flex-1 min-w-0"><span className="block text-[15px] text-[var(--ds-text)] truncate">{t.title}</span><DomeinLabel domein={t.project} /></span>
                      <span className="text-[12px] tabular-nums text-[var(--ds-text-soft)] shrink-0">{wanneer}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </TaakSheet>
        )}
        {sheetLijst === "routines" && (
          <TaakSheet open onClose={() => setSheetLijst(null)} titel={`Routines · ${recurringTasks.filter(t => t.isActive).length}`} kleur="streef"
            acties={[{ label: "Nieuwe routine", kleur: "accent", onClick: () => { setSheetLijst(null); setEditingRecurring(null); setShowRecurringModal(true); } }]}>
            <div className="-mx-5">
              <RecurringTasksPanel templates={recurringTasks} onRefresh={() => { loadRecurring(); onTasksChange(); }} onEdit={t => { setSheetLijst(null); setEditingRecurring(t); setShowRecurringModal(true); }} />
            </div>
          </TaakSheet>
        )}
        {sheetLijst === "archief" && (
          <TaakSheet open onClose={() => setSheetLijst(null)} titel={parked.length ? `Geparkeerd · ${parked.length}` : `Gedaan · ${done.length}`} kleur="gedaan">
            <div className="-mx-5">
              {parked.length > 0 && (
                <div className="divide-y divide-[var(--ds-hairline)] border-b border-[var(--ds-hairline)]">
                  {parked.map(t => { const dl = t.hardDeadline ?? t.deadline; return <TaakRij key={t.id} kleur="zonder" titel={t.title} chip={dl ? ddmm(dl) : undefined} chipKleur="zonder" dim onOpen={() => { setSheetLijst(null); setSheetTaak(t); }} />; })}
                </div>
              )}
              <div className="px-5 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--ds-text-faint)]">Gedaan · {done.length}</div>
              {done.length === 0 ? <p className="px-5 pb-3 text-[14px] text-[var(--ds-text-faint)]">Nog niets afgerond.</p> : (
                <div className="divide-y divide-[var(--ds-hairline)]">
                  {done.slice(0, 40).map(t => <TaakRij key={t.id} kleur="gedaan" titel={t.title} chip={t.completedAt ? ddmm(t.completedAt.slice(0, 10)) : undefined} chipKleur="gedaan" dim sub={<DomeinLabel domein={t.project} />} onOpen={() => { setSheetLijst(null); setSheetTaak(t); }} />)}
                </div>
              )}
            </div>
          </TaakSheet>
        )}

        {/* ── Covey view (desktop) ── */}
        {viewMode === "covey" && (
          <div ref={coveyScrollRef} className="hidden sm:block flex-1 min-h-0 overflow-y-auto sm:overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:h-full">
              {COLS.map((col, idx) => {
                const colTaskList = colTasks(col.key);
                const isDrop      = dragOverCol === col.key && (!!draggedId || !!touchDrag);
                return (
                  <div
                    key={col.key}
                    data-quadrant={col.key}
                    onDragOver={e => {
                      e.preventDefault();
                      clearTimeout(leaveTimers.current[col.key]);
                      setDragOverCol(col.key);
                    }}
                    onDragLeave={() => {
                      leaveTimers.current[col.key] = setTimeout(() => setDragOverCol(null), 80);
                    }}
                    onDrop={() => handleDrop(col.key)}
                    className={`
                      flex flex-col sm:min-h-0
                      ${colTaskList.length === 0 && !draggedId && !touchDrag ? "sm:flex-none sm:w-[150px]" : "sm:flex-1 sm:min-w-0"}
                      ${idx < COLS.length - 1 ? "border-b sm:border-b-0 sm:border-r border-[var(--ds-hairline)]" : ""}
                      transition-all duration-100
                      ${isDrop ? `ring-1 ring-inset ${col.dropCls}` : ""}
                    `}
                  >
                    <div className="px-3 py-2.5 border-b border-[var(--ds-hairline)] shrink-0 bg-[var(--ds-surface)] sticky top-0 z-10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-baseline gap-2">
                          <span className={`text-sm font-bold ${col.textCls}`}>{col.label}</span>
                          <span className="text-[10px] text-[var(--ds-text-faint)] hidden sm:inline">{col.sub}</span>
                        </div>
                        <span className="text-xs text-[var(--ds-text-faint)] tabular-nums font-mono">{colTaskList.length}</span>
                      </div>
                    </div>
                    <div className="sm:flex-1 sm:overflow-y-auto p-2 space-y-2 min-h-[80px]">
                      {colTaskList.length === 0
                        ? <p className="text-[10px] text-[var(--ds-text-faint)] italic text-center pt-4">Leeg</p>
                        : colTaskList.map(task => (
                            <div key={task.id} onPointerDown={e => onCardPointerDown(e, task)} style={{ touchAction: "pan-y" }}>
                              <TaskCard
                                task={task}
                                onComplete={handleComplete}
                                onCancel={handleCancel}
                                onPark={handlePark}
                                onReopen={handleReopen}
                                onEdit={setEditingTask}
                                onUpdatePriority={handlePriority}
                                onDragStart={() => setDraggedId(task.id)}
                                onDragEnd={() => { setDraggedId(null); setDragOverCol(null); }}
                              />
                            </div>
                          ))
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Agenda view (desktop) ── */}
        {viewMode === "agenda" && (
          <div className="hidden sm:block flex-1 min-h-0 overflow-y-auto">
            {/* Legend */}
            <div className="flex items-center gap-4 px-3 py-1.5 border-b border-[var(--ds-hairline)] bg-[var(--ds-panel)] flex-wrap">
              {(Object.entries(TYPE_ICON) as [ScheduleType, typeof TYPE_ICON[ScheduleType]][]).map(([k, v]) => (
                <span key={k} className={`text-[10px] flex items-center gap-1 ${v.cls}`}>
                  <span>{v.icon}</span>
                  <span className="text-[var(--ds-text-faint)]">{v.label}</span>
                </span>
              ))}
            </div>

            {agendaGroups.length === 0 && (
              <p className="text-xs text-[var(--ds-text-faint)] italic text-center py-8">Geen taken gevonden</p>
            )}

            {agendaGroups.map(group => (
              <div key={group.key}>
                <div className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider ${group.headerCls} bg-[var(--ds-panel)] border-b border-[var(--ds-hairline)] sticky top-0 z-10 flex items-center justify-between`}>
                  <span>{group.label}</span>
                  <span className="font-normal opacity-60">{group.tasks.length}</span>
                </div>
                <div>
                  {group.tasks.map(task => (
                    <AgendaTaskRow
                      key={task.id}
                      task={task}
                      onEdit={setEditingTask}
                      onComplete={handleComplete}
                      onPark={handlePark}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Routines, geparkeerd en gedaan staan sinds 10 okt 2026 achter "Meer" (zelfde sheets als mobiel) */}
      </div>

      {/* Sleep-ghost (touch) — volgt de vinger; toont waar je 'm laat vallen */}
      {touchDrag && (
        <div
          className="fixed z-[100] pointer-events-none px-3 py-2 rounded-lg bg-[var(--ds-panel)] shadow-2xl border-2 border-[var(--accent)] text-xs font-medium text-[var(--ds-text)] max-w-[70vw] truncate"
          style={{ left: touchDrag.x, top: touchDrag.y, transform: "translate(-50%, -130%)" }}
        >
          {touchDrag.title}
          {touchDrag.q && <span className="ml-1.5 text-[10px] font-bold text-[var(--accent)]">→ {COLS.find(c => c.key === touchDrag.q)?.label ?? touchDrag.q}</span>}
        </div>
      )}

      {/* ADR-0081 — laag 2 op mobiel: alle feiten en acties van één taak */}
      {sheetTaak && (() => {
        const t = sheetTaak;
        const dl = t.hardDeadline ?? t.deadline;
        const isDone = t.status === "done" || t.status === "cancelled";
        const isParked = t.status === "parked";
        const q = (t.coveyQuadrant ?? "Q2") as CoveyQuadrant;
        const lang = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" });
        return (
          <TaakSheet open onClose={() => setSheetTaak(null)} titel={t.title} kleur={isLedgndTaak(t) ? "ledgnd" : Q_SEM[q]}
            feiten={[
              ...(dl ? [{ label: "Deadline", waarde: <span style={{ color: dl < vandaagISO ? "var(--sem-overdue)" : undefined }}>{lang(dl)}</span> }] : []),
              ...(t.softDeadline ? [{ label: "Streefdatum", waarde: lang(t.softDeadline) }] : []),
              ...(t.plannedStart ? [{ label: "Gepland", waarde: new Date(t.plannedStart).toLocaleString("nl-NL", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Amsterdam" }) }] : []),
              ...(t.estimatedMinutes ? [{ label: "Tijd", waarde: `${t.estimatedMinutes} min` }] : []),
              ...(t.necessity?.why ? [{ label: "Waarom", waarde: t.necessity.why }] : []),
              ...(t.nextAction ? [{ label: "Volgende stap", waarde: t.nextAction }] : []),
              ...(t.description ? [{ label: "Notitie", waarde: <span className="whitespace-pre-line line-clamp-4">{t.description}</span> }] : []),
              ...(isLedgndTaak(t) ? [{ label: "Bron", waarde: "Ledgnd" }] : []),
            ]}
            acties={[
              isDone || isParked
                ? { label: "Heropenen", kleur: "accent", onClick: () => { handleReopen(t.id); setSheetTaak(null); } }
                : { label: "Klaar", kleur: "gedaan", onClick: () => { handleComplete(t.id); setSheetTaak(null); } },
              ...(!isDone && !isParked ? [{ label: "Parkeren", kleur: "binnenkort" as const, onClick: () => { handlePark(t.id); setSheetTaak(null); } }] : []),
              { label: "Bewerk", onClick: () => { setEditingTask(t); setSheetTaak(null); } },
              { label: "Verwijder", kleur: "danger", onClick: () => { if (confirm("Taak verwijderen?")) { handleCancel(t.id); setSheetTaak(null); } } },
            ]}>
            {!isDone && !isParked && !isLedgndTaak(t) && (
              <SoortKeuze taak={t} opslaan={async body => {
                const r = await fetch(`/api/tasks/${t.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
                if (!r.ok) return false;
                const j = await r.json() as { task?: MentorTask };
                if (j.task) setSheetTaak(j.task);
                onTasksChange();
                return true;
              }} />
            )}
            {!isDone && (
              <div className="mb-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide mb-2 text-[var(--ds-text-faint)]">Domein</div>
                <div className="flex flex-wrap gap-2">
                  {DOMEINEN.map(d => { const on = t.project === d; return (
                    <button key={d} onClick={() => zetDomein(t.id, d)} aria-pressed={on}
                      className="min-h-[44px] px-3.5 rounded-xl text-[13px] font-semibold inline-flex items-center active:opacity-60 transition-opacity"
                      style={on ? { background: "var(--ds-text)", color: "#fff" } : { background: "var(--ds-hover)", color: "var(--ds-text)" }}>
                      {d}
                    </button>
                  ); })}
                </div>
              </div>
            )}
            {!isDone && (
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide mb-2 text-[var(--ds-text-faint)]">Kwadrant</div>
                <div className="grid grid-cols-4 gap-2">
                  {/* ADR-0099: keuze = donker vlak, geen kwadrantkleur. */}
                  {COLS.map(c => { const on = c.key === q; return (
                    <button key={c.key} onClick={() => { moveToQuadrant(t.id, c.key); setSheetTaak(null); }} aria-pressed={on}
                      className="min-h-[44px] rounded-xl text-[13px] font-semibold active:opacity-60 transition-opacity"
                      style={on ? { background: "var(--ds-text)", color: "#fff" } : { background: "var(--ds-hover)", color: "var(--ds-text)" }}>{c.label}</button>
                  ); })}
                </div>
              </div>
            )}
          </TaakSheet>
        );
      })()}

      {showPlus && (
        <TaakSheet open onClose={() => setShowPlus(false)} titel="Nieuw" acties={[
          { label: "Taak", kleur: "accent", onClick: () => { setShowPlus(false); setShowCreateModal(true); } },
          { label: "Routine", onClick: () => { setShowPlus(false); setEditingRecurring(null); setShowRecurringModal(true); } },
        ]} />
      )}

      {showCreateModal && (
        <TaskCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { onTasksChange(); setShowCreateModal(false); }}
        />
      )}

      <TaskEditorModal
        task={editingTask}
        onClose={() => setEditingTask(null)}
        onSave={handleSaveEdit}
      />

      {showRecurringModal && (
        <RecurringTaskModal
          template={editingRecurring}
          onClose={() => { setShowRecurringModal(false); setEditingRecurring(null); }}
          onSaved={() => { loadRecurring(); onTasksChange(); }}
        />
      )}
    </>
  );
}
